import {
  type ProductDetailDrawerDto,
  ProductDetailDrawerRequestDto,
  ProductDetailDto,
  ProductSpecificationDto,
} from "@lib/ShiOneClient";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  createSavedCurrencyFormatter,
  isKnownCurrency,
} from "shared-ui/src/functions/currency";
import { formatCurrency as sharedFormatCurrency } from "shared-ui/src/components/ecommerce/helpers/EcommerceCurrencyFormatHelper";
import { ProductDetailDrawer } from "shared-ui/src/components/ecommerce/product-detail-drawer/ProductDetailDrawer";
import { ProductDrawerTab } from "shared-ui/src/components/ecommerce/product-detail-drawer/types";
import { getAddToCartDisabledReason } from "shared-ui/src/components/ecommerce/product-detail-drawer/addToCartDisabledReason";
import { useProductDetailDrawer } from "shared-ui/src/components/ecommerce/hooks/useProductDetailDrawer";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import useQuoteProductDetailDrawer from "@features/ecommerce/hooks/useQuoteProductDetailDrawer";
import useProductAvailability from "@features/ecommerce/hooks/useProductAvailability";
import AddToCartModal from "@features/ecommerce/cart/AddToCartModal";
import useAddToCartModal from "@features/ecommerce/cart/useAddToCartModal";
import { useGetQuoteProductDetailDrawerImages } from "@features/ecommerce/hooks/useGetQuoteProductDetailDrawerImages";
import { preserveSavedLinePrice } from "@features/ecommerce/helpers/preserveSavedLinePrice";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";

export interface QuoteProductDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  product: ProductDetailDto | null;
  request: ProductDetailDrawerRequestDto | null;
  currencyId?: number;
  /** Legacy display format, used only when `currencyId` is missing or unknown. */
  currencyFormat?: string;
  /** Optional z-index override; raise above a host Dialog when opened from within one. */
  zIndex?: number;
  /** Tab shown when the drawer opens; a routed host passes the tab from the URL. */
  initialTab?: ProductDrawerTab;
  onTabChange?: (tab: ProductDrawerTab) => void;
  /**
   * Called once per product when its details fail to load or the catalog has no record. Omit
   * it to keep showing the basic product info passed in `product`.
   */
  onDetailUnavailable?: () => void;
}

/**
 * Quotes thin wrapper around {@link ProductDetailDrawer}. Delegates data orchestration to
 * {@link useProductDetailDrawer}: product enrichment, deferred-image retry flow, and SHI.com
 * product URL construction. Provides the quantity selector + add-to-cart and the Stock /
 * Inventory tab, matching the Standards Shop drawer; availability is fetched lazily when the
 * tab is opened.
 */
export function QuoteProductDetailDrawer({
  open,
  onClose,
  product,
  request,
  currencyId,
  currencyFormat,
  zIndex,
  initialTab,
  onTabChange,
  onDetailUnavailable,
}: QuoteProductDetailDrawerProps) {
  const formatPrice = useMemo(() => {
    if (!isKnownCurrency(currencyId) && currencyFormat) {
      return (amount: number) => sharedFormatCurrency(amount, currencyFormat);
    }
    const formatter = createSavedCurrencyFormatter(currencyId);
    return (amount: number) => formatter.format(amount);
  }, [currencyId, currencyFormat]);
  const {
    isLoading,
    isError,
    hasProductDetail,
    enrichedProduct,
    images,
    specification,
    fullSpecification,
    productDetailUrl,
  } = useProductDetailDrawer<
    ProductDetailDto,
    ProductDetailDrawerRequestDto,
    ProductSpecificationDto,
    ProductDetailDrawerDto
  >({
    product,
    request,
    shiComBaseUrl: getShiComBaseUrl(),
    useGetProductDetailDrawer: useQuoteProductDetailDrawer,
    useGetProductDetailDrawerImages: useGetQuoteProductDetailDrawerImages,
  });

  const quoteProduct = useMemo(
    () => preserveSavedLinePrice(enrichedProduct, product?.price),
    [enrichedProduct, product?.price],
  );

  const orgUnitId = request?.orgunitId ?? null;
  const productDescriptor = request?.productDescriptor ?? null;
  const productClass = request?.productClass ?? null;

  const unavailableKey =
    request != null && !isLoading && (isError || !hasProductDetail)
      ? `${productClass ?? ""}~${productDescriptor ?? ""}`
      : null;
  const reportedUnavailableKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !open ||
      !onDetailUnavailable ||
      unavailableKey == null ||
      reportedUnavailableKeyRef.current === unavailableKey
    ) {
      return;
    }
    reportedUnavailableKeyRef.current = unavailableKey;
    onDetailUnavailable();
  }, [open, onDetailUnavailable, unavailableKey]);

  const startsOnStock = initialTab === ProductDrawerTab.StockInventory;
  const [stockActivated, setStockActivated] = useState(startsOnStock);
  useEffect(() => {
    setStockActivated(startsOnStock);
    // A routed tab switch changes `initialTab`; only a new product resets activation.
  }, [productDescriptor]);
  useEffect(() => {
    if (startsOnStock) setStockActivated(true);
  }, [startsOnStock]);

  const availability = useProductAvailability(
    productDescriptor,
    orgUnitId,
    open && stockActivated && productDescriptor != null && orgUnitId != null,
  );

  const { hasPermission } = useClaims();
  const { addToCart, modalProps } = useAddToCartModal();
  const addToCartDisabledReason = getAddToCartDisabledReason({
    isLoading,
    isError,
    // Matches the add endpoint's ShoppingCartSave gate (EcommerceShoppingCartController "add").
    hasCartPermission: hasPermission(ShiOnePermission.shoppingCartSave),
    hasProductIdentifiers: !!productDescriptor && !!productClass,
    canAddToCart: enrichedProduct?.canAddToCart,
    containedInCatalog: enrichedProduct?.containedInCatalog,
    hasCatalogDetail: enrichedProduct?.hasCatalogDetail,
  });
  const canAddToCart = addToCartDisabledReason == null;

  return (
    <>
      <ProductDetailDrawer
        open={open}
        onClose={onClose}
        loading={isLoading}
        product={quoteProduct}
        images={images}
        fullSpecification={fullSpecification}
        specificationFallback={specification}
        productDetailUrl={productDetailUrl}
        telemetrySection="quotes_quick_view"
        formatPrice={formatPrice}
        initialTab={initialTab}
        onTabChange={(tab) => {
          if (tab === ProductDrawerTab.StockInventory) setStockActivated(true);
          onTabChange?.(tab);
        }}
        cart={{
          canAddToCart,
          disabledReason: addToCartDisabledReason,
          onAddToCart: (quantity) => {
            if (!productDescriptor || !productClass) return;
            addToCart({
              id: productDescriptor,
              productDescriptor,
              productClass,
              name: enrichedProduct?.productName ?? null,
              price: enrichedProduct?.price ?? null,
              imageUrl: images[0]?.url ?? null,
              quantity,
            });
          },
        }}
        stockTab={{
          data: availability.data,
          isLoading: availability.isLoading,
          isError: availability.isError,
          onRefresh: availability.refetch,
        }}
        zIndex={zIndex}
      />
      <AddToCartModal {...modalProps} />
    </>
  );
}
